import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFile } from "@fortawesome/free-regular-svg-icons";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { DoraLinkButton, DoraButton } from "dora-shared/frontend";
import "./DocumentsList.scss";
import { Popover } from "@mui/material";
import { MouseEventHandler, useState } from "react";
import { Document } from "../../../../ducks/data/cargo-documents/types";

type DocumentsListProps = {
  documents: Document[];
  previewPosition: "left" | "right";
  onRemoveItem: (id: string) => void;
  controlsDisabled?: boolean;
  previewMode: boolean;
  className?: string;
};

const DocumentsList = (props: DocumentsListProps) => {
  const {
    documents,
    onRemoveItem,
    controlsDisabled,
    className,
    previewPosition,
    previewMode,
  } = props;

  const classes = clsx("uploaded-documents-list", className);

  return (
    <div className={classes}>
      {documents.map((f) => (
        <DocumentListItem
          previewPosition={previewPosition}
          onRemoveItem={onRemoveItem}
          controlsDisabled={controlsDisabled}
          previewMode={previewMode}
          f={f}
          key={f.id}
        />
      ))}
    </div>
  );
};

const DocumentListItem = ({
  f,
  onRemoveItem,
  controlsDisabled,
  previewPosition,
  previewMode,
}: {
  f: Document;
  onRemoveItem: (id: string) => void;
  controlsDisabled?: boolean;
  previewPosition: "left" | "right";
  previewMode: boolean;
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { t } = useTranslation("components");
  const [loaded, setLoaded] = useState(false);
  const [signedPreviewUrl, setSignedPreviewUrl] = useState<string | null>(null);
  const [signedDownloadUrl, setSignedDownloadUrl] = useState<string | null>(
    null
  );

  // Fetch signed preview URL on hover if not already cached
  const handlePopoverOpen: MouseEventHandler<HTMLButtonElement> = (event) => {
    setAnchorEl(event.currentTarget);

    if (f.urlPreviewNew && !signedPreviewUrl) {
      fetch(f.urlPreviewNew)
        .then((res) => res.json())
        .then((data) => setSignedPreviewUrl(data.url))
        .catch((err) =>
          console.error("Failed to fetch signed preview URL", err)
        );
    }
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);

  const classes = clsx("document-list-item__preview", {
    "document-list-item__preview--left": previewPosition === "left",
    "document-list-item__preview--right": previewPosition === "right",
  });

  // Fetch signed download URL on click if not already cached
  const onFileClick = async () => {
    if (signedDownloadUrl) {
      window.open(signedDownloadUrl, "_blank");
    } else if (f.urlNew) {
      const response = await fetch(f.urlNew);
      const data = await response.json();
      setSignedDownloadUrl(data.url);
      window.open(data.url, "_blank");
    }
  };

  return (
    <div key={f.id} className="uploaded-documents-list__item">
      <div className="flex items-center gap-2">
        <div className="flex-grow">
          <DoraLinkButton
            onMouseEnter={handlePopoverOpen}
            onMouseLeave={handlePopoverClose}
            className="flex items-center gap-2 whitespace-nowrap"
            onClick={onFileClick}
            role="link"
          >
            <FontAwesomeIcon icon={faFile} />
            <div className="max-w-sm truncate">{f.name}</div>
          </DoraLinkButton>
          {f.urlPreview ? (
            <Popover
              open={open}
              anchorEl={anchorEl}
              onClose={handlePopoverClose}
              anchorOrigin={
                previewPosition === "left"
                  ? { vertical: "center", horizontal: "left" }
                  : {
                      vertical: "center",
                      horizontal: "right",
                    }
              }
              transformOrigin={
                previewPosition === "left"
                  ? { vertical: "center", horizontal: "right" }
                  : {
                      vertical: "center",
                      horizontal: "left",
                    }
              }
              disableRestoreFocus
              sx={{ pointerEvents: "none" }}
              PaperProps={{
                style: {
                  pointerEvents: "none",
                  backgroundColor: "transparent",
                  boxShadow: "none",
                },
              }}
            >
              <div className={classes}>
                <img
                  src={signedPreviewUrl || f.urlPreviewNew!}
                  alt="Preview"
                  style={{ display: loaded ? "block" : "none" }}
                  onLoad={() => setLoaded(true)}
                />
                {!loaded && <div>Loading...</div>}
              </div>
            </Popover>
          ) : null}
        </div>
        {!previewMode && (
          <DoraButton
            className="document-list-item__remove"
            disabled={controlsDisabled}
            aria-label={t("buttonLabels.remove")}
            variant="ghost"
            onClick={() => onRemoveItem(f.id)}
          >
            <FontAwesomeIcon icon={faTimes} />
          </DoraButton>
        )}
      </div>
    </div>
  );
};

export default DocumentsList;
